import { yupResolver } from '@hookform/resolvers/yup';
import { useConfig } from 'config';
import { useAuth } from 'contexts/auth';
import React, { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import emailValidationSchema from 'validations/email';
import * as yup from 'yup';
import { createBackInStockSubscription } from '../../../../../../api/back-in-stock';
import IconCross from '../../../../../../assets/icons/cross.svg';
import Button from '../../../../../../components/button';
import Input from '../../../../../../components/form/input';
import Select from '../../../../../../components/form/select';
import Modal from '../../../../../../components/modal';

interface PropTypes {
  isOpen: boolean;
  onClose: () => any;
  variants: Array<any>;
  selectedVariant: any;
  title: string;
}
interface FormState {
  variantId: string;
  email: string;
}

const BackInStockModal: React.FC<PropTypes> = ({
  selectedVariant,
  title,
  isOpen,
  variants,
  onClose,
}) => {
  const { backInStockUrl } = useConfig();
  const { customer } = useAuth();

  const validationSchema = yup.object({
    email: emailValidationSchema,
  });

  const { register, handleSubmit, formState, control } = useForm<FormState>({
    defaultValues: {
      variantId: selectedVariant.id,
      email: customer ? customer.email : '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<FormState> = React.useCallback(
    async ({ variantId, email }: FormState) => {
      try {
        await createBackInStockSubscription({
          backInStockUrl,
          email,
          variantId,
        });
      } catch (error) {
        console.error({ error });
      } finally {
        onClose();
      }
    },
    [backInStockUrl, onClose]
  );

  const options = useMemo(() => {
    return variants
      .filter((variant) => !variant.availableForSale)
      .map((variant) => ({
        value: variant.id,
        label: variant.title,
      }));
  }, [variants]);

  return (
    <Modal
      className="max-w-xl px-10 py-8"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <button
        className="absolute top-4 right-4 z-20 p-2 opacity-50 hover:opacity-100 focus:outline-hidden"
        aria-label="Schließen"
        onClick={onClose}
      >
        <IconCross className="h-5 w-5" />
      </button>

      <h1 className="text-xl font-medium uppercase">
        <FormattedMessage id="pdp:buy-panel:back-in-stock:title" />
      </h1>

      <p className="mt-4">
        <FormattedMessage id="pdp:buy-panel:back-in-stock:description" />
      </p>

      <hr className="my-4" />

      <p className="text-lg">{title}</p>

      <form className="mt-6" onSubmit={handleSubmit(onSubmit)}>
        <Select
          options={options}
          formState={formState}
          control={control}
          name="variantId"
          style={{
            menuPortal: (styles) => ({
              ...styles,
              zIndex: 999999,
            }),
          }}
        />
        <Input
          aria-label="E-Mail"
          className="bg-other-white text-text-light-bg-secondary h-10 w-full rounded-sm border px-3 leading-none focus:outline-hidden"
          type="email"
          name="email"
          placeholder="E-Mail"
          register={register}
          formState={formState}
        />
        <Button
          loading={formState.isSubmitting}
          className="w-full"
          type="submit"
          layout="brand"
        >
          <FormattedMessage id="pdp:buy-panel:back-in-stock:cta" />
        </Button>
      </form>

      <p className="text-text-light-bg-secondary mt-6 text-sm">
        <FormattedMessage id="pdp:buy-panel:back-in-stock:terms" />
      </p>
    </Modal>
  );
};

export default BackInStockModal;
